// alert-groups.js
//
// Shared rules for "which of my alerts apply to this sensor, grouped by reading
// type". Extracted from the manage-sensor page's alerts section so the setup
// onboarding review step groups and labels alerts exactly the same way — the two
// surfaces describing the same alerts differently would be worse than either.
//
// Icons are names from util/icon.js (not Font Awesome) so they render without the
// remote, domain-locked FA kit.

import getProbeSensorTypes from './get-probe-sensor-types'
import getPowerSensorTypes from './get-power-sensor-types'
import sensorHasLight from './sensor-has-light'
import sensorHasMotion from './sensor-has-motion'

// icon name + human title per reading type
export const READING_TYPE_META = {
  temperature: { title: 'Temperature', icon: 'temperature-half' },
  humidity: { title: 'Humidity', icon: 'droplet' },
  probe_temp: { title: 'Probe', icon: 'microchip' },
  connection: { title: 'Connection', icon: 'wifi' },
  power: { title: 'Power', icon: 'plug' },
  battery: { title: 'Battery', icon: 'battery-half' },
  heat_index: { title: 'Heat Index', icon: 'temperature-high' },
  dew_point: { title: 'Dew Point', icon: 'droplet' },
  lux: { title: 'Light', icon: 'sun' },
  motion: { title: 'Motion', icon: 'person-running' },
};

// stable card order regardless of alert ids, matching the manage-sensor page
const CARD_ORDER = [
  'probe_temp',
  'temperature',
  'humidity',
  'heat_index',
  'dew_point',
  'lux',
  'motion',
  'connection',
  'power',
  'battery',
];

// alert types whose trigger condition is fixed rather than a user-set threshold;
// repeating "below 0" under these would be noise
const FIXED_CONDITION_TYPES = ['connection', 'power', 'battery', 'motion'];

export const conditionIsFixed = alert => FIXED_CONDITION_TYPES.includes(alert.reading_type);

// an alert pushed down from an enterprise parent account
export const isManagedAlert = alert => !!alert.enterprise_alert_id;

export const isGlobalAlert = alert => String(alert.sensors_global) === '1';

export const isPausedAlert = alert => String(alert.enabled) === '0';

// could this alert type ever apply to this sensor's hardware?
export const alertIsCompatible = (sensor, alert) => {

  // with no sensor record to check against, only exclude nothing — better to
  // show an alert that turns out not to apply than to hide one that does
  if (!sensor) {
    return true;
  }

  switch (alert.reading_type) {
    case 'probe_temp':
      return !!getProbeSensorTypes()[sensor.type];
    case 'power':
      return !!getPowerSensorTypes()[sensor.type];
    case 'lux':
      return sensorHasLight(sensor) === true;
    case 'motion':
      return sensorHasMotion(sensor) === true;
    default:
      return true;
  }

};

// does this alert currently apply to this sensor?
export const alertAppliesToSensor = (sensor, alert) => {

  if (!alertIsCompatible(sensor, alert)) {
    return false;
  }

  if (isGlobalAlert(alert)) {
    return true;
  }

  // the sensor record carries assigned alert ids; the alert also carries its
  // sensor list, and either side is enough to establish the link
  const assignedToSensor = sensor && Array.isArray(sensor.alerts) &&
    sensor.alerts.map(String).includes(String(alert.id));

  if (assignedToSensor) {
    return true;
  }

  const sensorKey = sensor ? String(sensor.sensor_id || sensor.sid || '') : '';

  return !!sensorKey && Array.isArray(alert.sensors) &&
    alert.sensors.some(s => String(s.sensor_id) === sensorKey);

};

/**
 * Group the alerts that apply to a sensor into one entry per reading type.
 *
 * @param {object|null} sensor  sensor record, or null when it isn't loaded yet
 * @param {Array|object} alerts array of alerts, or the id-keyed map getAlerts(false) returns
 * @param {object} [meta]  per-reading-type overrides of READING_TYPE_META, e.g.
 *                         { probe_temp: { title, icon } }, for a surface that
 *                         names a reading after what the person just set up
 * @returns {Array} [{ key, title, icon, alerts: [...] }]
 */
export const groupAlertsByType = (sensor, alerts, meta = {}) => {

  const list = Array.isArray(alerts)
    ? alerts
    : Object.keys(alerts || {}).map(id => alerts[id]);

  const groups = [];

  list
    .filter(alert => alertAppliesToSensor(sensor, alert))
    .forEach(alert => {

      const base = READING_TYPE_META[alert.reading_type];

      if (!base) {
        return;
      }

      const typeMeta = { ...base, ...(meta[alert.reading_type] || {}) };

      let group = groups.find(g => g.key === alert.reading_type);

      if (!group) {
        group = { key: alert.reading_type, title: typeMeta.title, icon: typeMeta.icon, alerts: [] };
        groups.push(group);
      }

      group.alerts.push(alert);

    });

  groups.forEach(group => {
    group.alerts.sort((a, b) =>
      String(a.alert_name || '').localeCompare(String(b.alert_name || ''))
    );
  });

  groups.sort((a, b) => CARD_ORDER.indexOf(a.key) - CARD_ORDER.indexOf(b.key));

  return groups;

};

/**
 * Human-readable trigger condition, in the user's temperature preference.
 * Returns HTML (it contains &deg;), so call sites must not escape it.
 */
export const formatCondition = alert => {

  const value = alert.reading_value;

  switch (alert.reading_type) {
    case 'connection':
      return 'Connection Lost/Found';
    case 'temperature':
    case 'probe_temp':
    case 'heat_index':
      return `${alert.reading_condition} ${window.celsiusToUserTemp(parseFloat(value), 1)}&deg;${window.getUserTempPref()}`;
    case 'dew_point':
      return `within ${window.celsiusUnitsToUserTemp ? window.celsiusUnitsToUserTemp(value, 1) : value}&deg;${window.getUserTempPref()}`;
    case 'humidity':
      return `${alert.reading_condition} ${parseInt(value)}%`;
    case 'battery':
      return `Battery ${alert.reading_condition}`;
    case 'power':
      return 'Power Lost/Found';
    default:
      return `${alert.reading_condition} ${value}`;
  }

};

export default groupAlertsByType;
